/**
 * The four sample songs Solmi ships: the Samples list of the landing
 * composers (client/src/lib/composerSource.ts re-exports all four as
 * LANDING_SAMPLE_SONGS) and, since Oct 12 2026, "Songs made with Solmi" on
 * /ai-music-generator (the crawler HTML in server/_core/seo.ts and the React
 * page, client/src/components/AiMusicGeneratorSections.tsx), which shows the
 * ones marked `showcase` (SHOWCASE_SAMPLE_SONGS).
 *
 * They are Solmi's own songs, made with the song generator: the ids are
 * `music.generate` track ids, the files sit under media.solmi.ai/music/, and
 * `style` is the style prompt stored on the track. No other user's song goes
 * in this list.
 *
 * `showcase` is false for a song that stays in the composer but should not be
 * listed on a page built for search. "The Sound of Music" is the title of a
 * famous musical and film, so it is not shown there; "Sweet Dreams" is
 * generic enough to stay.
 *
 * `durationSeconds` is the length of the MP3 in whole seconds (ffprobe,
 * Oct 12 2026: 116.08, 220.67, 250.50 and 85.86 s; all four are 44.1 kHz
 * stereo MP3 at 256 kbps, the format server/minimaxApi.ts asks for). The
 * "How long are the songs?" answer on /ai-music-generator quotes the shortest
 * and the longest of the songs it shows, and a test keeps the two in step.
 */
export type LandingSampleSong = {
  id: string;
  title: string;
  /** Short label the landing composer shows under the title. */
  genre: string;
  /** The style prompt the song was generated with. */
  style: string;
  audioUrl: string;
  status: "completed";
  durationSeconds: number;
  /** Listed under "Songs made with Solmi" on /ai-music-generator. */
  showcase: boolean;
};

export const LANDING_SAMPLE_SONG_DATA: LandingSampleSong[] = [
  {
    id: "track_1778078829330_4y76r1xn2",
    title: "Paper Boats",
    genre: "Folk, Longing, female vocals",
    style: "Folk, Longing, female vocals",
    audioUrl: "https://media.solmi.ai/music/1778078892172.mp3",
    status: "completed",
    durationSeconds: 116,
    showcase: true,
  },
  {
    id: "track_1778068068825_yppbo9rp9",
    title: "Floating Home",
    genre: "Ethereal dream pop, female vocals",
    style:
      "ethereal dream pop, 74 BPM, soft pads, harp textures, breathy vocal, cinematic atmosphere, gentle percussion, pastel production, female vocals",
    audioUrl: "https://media.solmi.ai/music/1778068173623.mp3",
    status: "completed",
    durationSeconds: 220,
    showcase: true,
  },
  {
    // Composer sample only: the title is a famous musical and film.
    id: "track_1769154991306_x3lg0o0l2",
    title: "The Sound of Music",
    genre: "Classical",
    style: "Classical",
    audioUrl: "https://media.solmi.ai/music/1769155225296.mp3?v=20260817",
    status: "completed",
    durationSeconds: 250,
    showcase: false,
  },
  {
    id: "track_1764080309032_qim52cd97",
    title: "Sweet Dreams",
    genre: "Pop, Romantic, Quiet evening",
    style: "Pop, Romantic, Quiet evening",
    audioUrl: "https://media.solmi.ai/music/1764080409325.mp3?v=20260817",
    status: "completed",
    durationSeconds: 85,
    showcase: true,
  },
];

/** The samples /ai-music-generator lists: the ones marked `showcase`. */
export const SHOWCASE_SAMPLE_SONGS: LandingSampleSong[] =
  LANDING_SAMPLE_SONG_DATA.filter((song) => song.showcase);

/** "1:56" for 116 seconds. */
export function formatSampleSongDuration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
